import React, { useState } from "react";
import { useGlobalContext } from "./ScanViewerContext";
import styles from "./debug.module.scss";
import { GlobalLogger } from "./debugLogger";
import { cardScanPackageVersion } from "../version";

const DebugMenu = () => {
  const {
    bufferRef,
    threshold,
    setThreshold,
    modelState,
    scanDebugState,
    laplacianThreshold,
    scanFreqency,
    debugCanvasBestRef,
    bestScore,
    bestLaplacian,
    setBufferSize,
    setScanFreqency,
    latestScore,
    latestLaplacian,
    debugCanvasLatestRef,
    setLaplacianThreshold,
    bufferSize,
    debugMenu,
    videoMetadata,
    webviewVersion,
    mobileSdkVersion
  } = useGlobalContext();
  const [showDebugMenu, setShowDebugMenu] = useState(false);

  const loadDebugLibraries = async (): Promise<void> => {
    //Load debug libraries in dynamically at runtine so we don't need them in the package.

    return new Promise((resolve, reject) => {
      if (debugMenu == false) {
        return reject();
      }

      // @ts-expect-error: Debug libraries loading dynamically.
      if (window.JSZip != undefined || window.saveAs != undefined) {
        //already loaded
        return resolve();
      }

      console.log("JSZip & FileSaver are required for this feature");

      // @ts-expect-error: FileSaver loading dynamically.
      if (window.FileSaver == undefined) {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/FileSaver.js/2.0.0/FileSaver.min.js";
        script.id = "filesaver-dynamic-load";
        document.body.appendChild(script);
      }

      // @ts-expect-error: JSZip loading dynamically.
      if (window.JSZip == undefined) {
        const script = document.createElement("script");
        script.src =
          "https://cdnjs.cloudflare.com/ajax/libs/jszip/3.7.1/jszip.min.js";
        script.id = "jszip-dynamic-load";
        document.body.appendChild(script);

        script.onload = () => {
          console.log("JSZip loaded!");
          return resolve();
        };
      }
    });
  };

  const downloadDebugLog = (event: any) => {
    if (bufferRef.current.length == 0) {
      GlobalLogger.log("No cards to download");
    }

    loadDebugLibraries().then(() => {
      // @ts-expect-error: Debug libraries loading dynamically.
      const zip = new window.JSZip();

      bufferRef.current.forEach((element: any) => {
        const name = element.file.lastModified + ".jpg";
        zip.file(name, element.file);
      });

      const files = bufferRef.current.map((element: any) => {
        element["file"] = element.file.lastModified;
        return element;
      });

      const data = {
        package_name: "insurance-cardscan-react",
        package_version: cardScanPackageVersion,
        ...(webviewVersion ? {webviewVersion: webviewVersion} : {}),
        ...(mobileSdkVersion ? {mobileSdkVersion: mobileSdkVersion} : {}),
        windowInner: {
          width: window.innerWidth,
          height: window.innerHeight,
        },
        files: files,
        debugLog: GlobalLogger.getLog(),
        videoMetadata: videoMetadata,
        scanAttributes: {
          modelState: modelState,
          scanDebugState: scanDebugState,
          threshold: threshold,
          laplacianThreshold: laplacianThreshold,
          scanFreqency: scanFreqency,
          bufferSize: bufferSize,
          bestScore: bestScore,
          bestLaplacian: bestLaplacian,
          latestScore: latestScore,
          latestLaplacian: latestLaplacian,
        },
      };

      const results = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json",
      });
      zip.file("debugLog.json", results);

      // Add canvases to the zip file
      if (debugCanvasBestRef.current) {
        const bestCanvasDataUrl =
          debugCanvasBestRef.current.toDataURL("image/jpeg");
        zip.file("bestCanvas.jpg", bestCanvasDataUrl.split(",")[1], {
          base64: true,
        });
      }
      if (debugCanvasLatestRef.current) {
        const latestCanvasDataUrl =
          debugCanvasLatestRef.current.toDataURL("image/jpeg");
        zip.file("latestCanvas.jpg", latestCanvasDataUrl.split(",")[1], {
          base64: true,
        });
      }

      const filename = "download_" + new Date().getTime() + ".zip";
      zip.generateAsync({ type: "blob" }).then(function (content: any) {
        // @ts-expect-error: Debug libraries loading dynamically.
        window.saveAs(content, filename);
      });
    });
  };

  if (!debugMenu) {
    return <div></div>;
  }

  return (
    <div data-cy="scanDebugMenu" className={styles.scanDebugMenu}>
      <button
        onClick={(e) => {
          setShowDebugMenu(!showDebugMenu);
        }}
      >
        {showDebugMenu ? "Hide" : "Show"}
      </button>
      <div className="scan-debug-wrapper" hidden={!showDebugMenu}>
        <div data-cy="scanDebugModelstate" className="scan-debug-modelstate">
          Model: {modelState}
        </div>
        <div data-cy="scanDebugScanstate" className="scan-debug-scanstate">
          Scanning: {scanDebugState}
        </div>
        <div className="scan-debug-threshold">
          Model Threshold:{" "}
          <input
            type="number"
            value={threshold}
            onChange={(event) => {
              setThreshold(Number(event.target.value));
            }}
          />
        </div>
        <div className="scan-debug-threshold">
          Laplacian Threshold:{" "}
          <input
            type="number"
            value={laplacianThreshold}
            onChange={(event) => {
              setLaplacianThreshold(Number(event.target.value));
            }}
          />
        </div>
        <div className="scan-debug-frequency">
          Scan Frequency:{" "}
          <input
            type="number"
            value={scanFreqency}
            onChange={(event) => {
              setScanFreqency(Number(event.target.value));
            }}
          />
        </div>
        <div className="scan-debug-results">
          Buffer: {bufferRef.current.length} /{" "}
          <input
            type="number"
            width="35px"
            value={bufferSize}
            onChange={(event) => {
              setBufferSize(Number(event.target.value));
            }}
          />
          <button
            onClick={(event) => {
              bufferRef.current = [];
            }}
          >
            Clear
          </button>
        </div>

        <div className="scan-debug-best-score">
          Best Score: {(Math.round(bestScore * 1000) / 1000).toFixed(3)}
          <br />
          Best Laplacian: {(Math.round(bestLaplacian * 1000) / 1000).toFixed(3)}
        </div>
        <canvas ref={debugCanvasBestRef} width="200" height="125"></canvas>

        <div className="scan-debug-latest-score">
          Latest Score: {(Math.round(latestScore * 1000) / 1000).toFixed(3)}{" "}
          <br />
          Laplacian: {(Math.round(latestLaplacian * 1000) / 1000).toFixed(3)}
        </div>
        <canvas ref={debugCanvasLatestRef} width="200" height="125"></canvas>
        <div className="scan-debug-get-debug">
          <button onClick={downloadDebugLog}>Download Debug Log</button>
        </div>
      </div>
    </div>
  );
};

export default DebugMenu;
